import { IconPercentage } from '@tabler/icons-react';
import NSIcon from 'bricks/NSIcon/NSIcon';
import React from 'react';
import { FormikProps } from 'formik';
import { NSCard, NSInput } from 'bricks';
import { BOESlideoutFormValues } from '../types';

interface IPropertySizeSectionProps {
    formik: FormikProps<BOESlideoutFormValues>;
    isApartmentWithRetail: boolean;
    totalNetRentable: number;
    netRentable: number;
    formatNumber: (number: number) => string;
}

const PropertySizeSection: React.FC<IPropertySizeSectionProps> = ({
    formik,
    isApartmentWithRetail,
    totalNetRentable,
    netRentable,
    formatNumber,
}) => {
    const {
        values, handleChange, handleBlur, setFieldValue, errors, touched,
    } = formik;

    return (
        <NSCard className="p-3 w-50">
            <span className="mr-2 mb-1 text-primary font-weight-bold BOESlideoutForm__title">PROPERTY SIZE</span>
            {isApartmentWithRetail ? (
                <>
                    {/* Row 1: Built gross SF */}
                    <div className="d-flex justify-content-between">
                        <div className="flex-grow-1 mr-2">
                            <NSInput
                                id="grossSquareFootage"
                                type="number"
                                label="Apartment built gross SF"
                                name="grossSquareFootage"
                                placeholder="Enter amount"
                                appendInputAddon={<span>SF</span>}
                                value={values.grossSquareFootage}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                errorMsg={touched.grossSquareFootage && errors.grossSquareFootage ? errors.grossSquareFootage : null}
                                showErrorMsg={!!(touched.grossSquareFootage && errors.grossSquareFootage)}
                            />
                        </div>
                        <div className="flex-grow-1">
                            <NSInput
                                id="retailGrossSquareFootage"
                                type="number"
                                label="Retail built gross SF"
                                name="retailGrossSquareFootage"
                                placeholder="Enter amount"
                                appendInputAddon={<span>SF</span>}
                                value={values.retailGrossSquareFootage}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                errorMsg={
                                    touched.retailGrossSquareFootage && errors.retailGrossSquareFootage
                                        ? errors.retailGrossSquareFootage : null
                                }
                                showErrorMsg={!!(touched.retailGrossSquareFootage && errors.retailGrossSquareFootage)}
                            />
                        </div>
                    </div>
                    {/* Row 2: Efficiency */}
                    <div className="d-flex justify-content-between mt-3">
                        <div className="flex-grow-1 mr-2">
                            <NSInput
                                id="efficiency"
                                label="Apartment efficiency"
                                name="efficiency"
                                placeholder=""
                                appendInputAddon={<NSIcon icon={IconPercentage} />}
                                type="number"
                                inputClassName="mb-1"
                                value={values.efficiency}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                    const newValue = Math.min(Math.max(event.target.valueAsNumber, 0), 100);
                                    setFieldValue('efficiency', newValue);
                                }}
                                onBlur={handleBlur}
                                errorMsg={touched.efficiency && errors.efficiency ? errors.efficiency : null}
                                showErrorMsg={!!(touched.efficiency && errors.efficiency)}
                                includeRange
                            />
                        </div>
                        <div className="flex-grow-1">
                            <NSInput
                                id="retailEfficiency"
                                label="Retail efficiency"
                                name="retailEfficiency"
                                placeholder=""
                                appendInputAddon={<NSIcon icon={IconPercentage} />}
                                type="number"
                                inputClassName="mb-1"
                                value={values.retailEfficiency}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                    const newValue = Math.min(Math.max(event.target.valueAsNumber, 0), 100);
                                    setFieldValue('retailEfficiency', newValue);
                                }}
                                onBlur={handleBlur}
                                errorMsg={touched.retailEfficiency && errors.retailEfficiency ? errors.retailEfficiency : null}
                                showErrorMsg={!!(touched.retailEfficiency && errors.retailEfficiency)}
                                includeRange
                            />
                        </div>
                    </div>
                    {/* Row 3: Net rentable SF */}
                    <div className="mt-2">
                        Net rentable SF:
                        {' '}
                        <span className="text-white">{formatNumber(totalNetRentable)}</span>
                    </div>
                </>
            ) : (
                <div className="d-flex justify-content-between">
                    <div className="flex-grow-1 mr-2">
                        <NSInput
                            id="grossSquareFootage"
                            type="number"
                            label="Built gross SF"
                            name="grossSquareFootage"
                            placeholder="Enter amount"
                            appendInputAddon={<span>SF</span>}
                            value={values.grossSquareFootage}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            errorMsg={touched.grossSquareFootage && errors.grossSquareFootage ? errors.grossSquareFootage : null}
                            showErrorMsg={!!(touched.grossSquareFootage && errors.grossSquareFootage)}
                        />
                    </div>
                    <div className="flex-grow-2">
                        <NSInput
                            id="efficiency"
                            label="Efficiency ratio"
                            name="efficiency"
                            placeholder=""
                            appendInputAddon={<NSIcon icon={IconPercentage} />}
                            type="number"
                            inputClassName="mb-1"
                            value={values.efficiency}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                const newValue = Math.min(Math.max(event.target.valueAsNumber, 0), 100);
                                setFieldValue('efficiency', newValue);
                            }}
                            onBlur={handleBlur}
                            errorMsg={touched.efficiency && errors.efficiency ? errors.efficiency : null}
                            showErrorMsg={!!(touched.efficiency && errors.efficiency)}
                            includeRange
                        />
                        <div className="mt-1">
                            Net rentable SF:
                            {' '}
                            <span className="text-white">{formatNumber(netRentable)}</span>
                        </div>
                    </div>
                </div>
            )}
        </NSCard>
    );
};

export default PropertySizeSection;
